import { useEffect, useState, useCallback } from 'react';
// @mui
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import Checkbox from '@mui/material/Checkbox';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import AvatarGroup from '@mui/material/AvatarGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Unstable_Grid2';
import CircularProgress from '@mui/material/CircularProgress';
// routes
import { paths } from 'src/routes/paths'; 
import { useParams } from 'src/routes/hook';
import { RouterLink } from 'src/routes/components';
// utils
import { fDate } from 'src/utils/format-time';
import { fShortenNumber } from 'src/utils/format-number';

import { HTBaiViet } from 'src/types/htblog';
// components
import Iconify from 'src/components/iconify';
import Markdown from 'src/components/markdown';
import EmptyContent from 'src/components/empty-content';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
// sections
import { useHome } from 'src/sections/home/hooks';
import BlogCommentList from '../blog-comment-list';
import BlogCommentForm from '../blog-comment-form';
import BlogDetailsHero from '../blog-details-hero';
import { PostDetailsSkeleton } from '../blog-skeleton';
// hooks (local)
import useBlog from '../hooks/use-blog';

// ----------------------------------------------------------------------

type IBlogCommentsModel = {
  id: string;
  blog_id: string;
  user_id: string;
  user_name: string;
  comment_text: string;
  date: Date | string;
};

// ----------------------------------------------------------------------

export default function BlogDetailsHomeView() {
  const params = useParams();
  const { slug } = params;
  const [comments, setComments] = useState<IBlogCommentsModel[]>([]);
  const [recentBlogs, setRecentBlogs] = useState<HTBaiViet[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingComments, setLoadingComments] = useState(false); 

  const { blog, blogStatus,getBlogHome } = useBlog();
  const { homeBlogs, getHomeBlogs } = useHome();

  const fetchBlog = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    try {
      await getBlogHome(slug);
    } catch (error) {
      console.error('Error fetching blog:', error);
    } finally {  
      setLoading(false);
    }
  }, [slug, getBlogHome]);

  const fetchComments = useCallback(async () => {  
    if (!slug) return;
    setLoadingComments(true);
    try {
      setComments([]);
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoadingComments(false);
    }
  }, [slug]);

  const fetchRecentBlogs = useCallback(async () => {
    try {
      await getHomeBlogs();
    } catch (error) {
      console.error('Error fetching recent blogs:', error);
    }
  }, [getHomeBlogs]);

  useEffect(() => {
    fetchBlog();
    fetchComments(); 
    fetchRecentBlogs();
  }, [fetchBlog, fetchComments, fetchRecentBlogs]);

  useEffect(() => {
    if (homeBlogs?.length) {
      const recents = homeBlogs
        .filter((r) => r.slug !== slug)
        .slice(0, 4) as HTBaiViet[];
      setRecentBlogs(recents);
    }
  }, [homeBlogs, slug]);

  if (loading || blogStatus.loading) {
    return <PostDetailsSkeleton />;
  }

  if (!blog) {
    return (
      <Container>
        <EmptyContent
          filled
          title="Không tìm thấy bài viết"
          action={
            <Button
              component={RouterLink}
              href={paths.blog.root}
              startIcon={<Iconify icon="eva:arrow-ios-back-fill" width={16} />}
              sx={{ mt: 3 }}
            >
              Quay lại danh sách
            </Button>
          }
          sx={{ py: 10 }}
        />
      </Container>
    );
  }

  return (
    <>
      <BlogDetailsHero
        title={blog.tieuDe}
        author={{
          name: blog.htTaiKhoan?.tenTaiKhoan || '',
          avatarUrl: `/assets/images/avatar/avatar_${Math.floor(Math.random() * 24) + 1}.jpg`,
        }}
        createdAt={blog.ngayTao}
        coverUrl={blog.hinhAnh}
      />

      <Container 
        maxWidth={false}
        sx={{
          py: 3,
          mb: 5,
          borderBottom: (theme) => `solid 1px ${theme.palette.divider}`,
        }}
      >
        <CustomBreadcrumbs
          links={[
            {
              name: 'Trang chủ',
              href: '/',
            },
            {
              name: 'Blog',
              href: paths.blog.root,
            },
            {
              name: blog.tieuDe,
            },
          ]}
          sx={{ maxWidth: 720, mx: 'auto' }}
        />
      </Container>
 
      <Container >
        <Grid container  spacing={{ xs: 6, md: 8 }}>
          <Grid xs={12} md={8}>
            <Stack>
              <Typography variant="h6" sx={{ mb: 5 }}>
                {blog.moTaNgan}
              </Typography>

              <Markdown children={blog.noiDung || ''} />

              <Stack
                spacing={3}
                sx={{
                  py: 3,
                  borderTop: (theme) => `dashed 1px ${theme.palette.divider}`,
                  borderBottom: (theme) => `dashed 1px ${theme.palette.divider}`,
                }}
              >
                <Stack direction="row" flexWrap="wrap" spacing={1}>
                  {(Array.isArray((blog as any).tags) ? (blog as any).tags : []).map((tag: string) => (
                    <Chip key={tag} label={tag} variant="soft" />
                  ))}
                </Stack> 

                <Stack direction="row" alignItems="center">
                  <FormControlLabel
                    control={
                      <Checkbox
                        defaultChecked
                        size="small"
                        color="error"
                        icon={<Iconify icon="solar:heart-bold" />}
                        checkedIcon={<Iconify icon="solar:heart-bold" />}
                      />
                    }
                    label={fShortenNumber((blog as any).like_count || 0)}
                    sx={{ mr: 1 }}
                  />

                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ typography: 'body2', color: 'text.disabled' }}>
                    <Iconify icon="solar:eye-bold" width={16} />
                    <span>{fShortenNumber(blog.luotXem || 0)}</span>
                  </Stack>
                </Stack>
              </Stack>

              <Stack direction="row" sx={{ mb: 3, mt: 5 }}>
                <Typography variant="h4" sx={{ flexGrow: 1 }}>
                  Bình luận ({(blog as any).comment_count || 0})
                </Typography>
              </Stack>

              <BlogCommentForm />

              <Divider sx={{ mt: 5, mb: 2 }} />

              {loadingComments ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <BlogCommentList comments={comments} />
              )}
            </Stack>
          </Grid>    
 
          <Grid xs={12} md={4}>
            <Stack spacing={5}>
              <Stack spacing={2}>
                <Typography variant="h5">Bài viết liên quan</Typography>

                {recentBlogs.map((recentBlog) => (
                  <Stack key={recentBlog.slug} spacing={2} direction="row">
                    <Box
                      component={RouterLink}
                      href={paths.blog.details(recentBlog.slug)}
                      sx={{
                        borderRadius: 1.5,
                        overflow: 'hidden',
                        position: 'relative',
                        display: 'block',
                        flexShrink: 0,
                      }}
                    >
                      <Box
                        component="img"
                        src={recentBlog.hinhAnh}
                        alt={recentBlog.tieuDe}
                        sx={{
                          width: 80,
                          height: 80,
                          objectFit: 'cover',
                        }}
                      />
                    </Box>

                    <Stack spacing={1} flexGrow={1}>
                      <Typography
                        component={RouterLink}
                        href={paths.blog.details(recentBlog.slug)}
                        variant="subtitle2"
                        sx={{
                          color: 'text.primary',
                          textDecoration: 'none',
                          '&:hover': { color: 'primary.main' },
                        }} 
                      >
                        {recentBlog.tieuDe}
                      </Typography>

                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.disabled',
                          display: '-webkit-box',
                          WebkitLineClamp: 2, 
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {recentBlog.moTaNgan}
                      </Typography>

                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <Iconify icon="solar:eye-bold" width={14} />
                          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                            {recentBlog.luotXem > 1000 ? `${(recentBlog.luotXem / 1000).toFixed(1)}k` : recentBlog.luotXem}
                          </Typography>
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <Iconify icon="eva:message-circle-fill" width={14} />
                          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                            {(recentBlog as any).comment_count || 0}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}